import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { RoundState, SessionMode, MediaType } from '@ember/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type MediaFormat = 'TEXT' | 'PHOTO' | 'AUDIO';

interface RoundCardProps {
  round: RoundState;
  /** ID du SessionPlayer courant (depuis localStorage). Null si on n'a pas pu le determiner. */
  playerId: string | null;
  /** Mode de jeu de la session. Si GUESS, le formulaire affiche une 2eme textarea pour la devine. */
  mode: SessionMode;
  /** En GUESS, on a besoin du pseudo de l'autre joueur pour le label "Ce que tu penses que X va dire". */
  otherPlayerPseudo?: string;
  /**
   * Callback de soumission. `mediaFile` et `mediaType` sont fournis si le joueur
   * a choisi de joindre une photo/audio (Dare). Le parent fait l'upload puis
   * envoie le filename via le socket.
   */
  onSubmit: (
    text: string,
    guessText?: string,
    mediaFile?: File,
    mediaType?: MediaType,
  ) => void | Promise<void>;
  onNext: () => void;
}

/**
 * Affichage d'un round selon son statut + le mode de session + le tag question.
 *
 * Modes texte vs Dare physique :
 *   - Si la question a `tagDare`, on affiche un selecteur de format
 *     (Texte / Photo / Audio). Photo/Audio = input file natif + preview.
 *   - Sinon : juste la textarea texte (comportement classique).
 *
 * Mode GUESS (transverse) : ajoute une 2eme textarea "ce que tu penses que X
 * va repondre" en plus du texte principal. Cumulable avec Dare en theorie
 * mais l'UX devient lourde (2 textareas + media), donc en GUESS on cache le
 * selecteur format Dare et on reste sur texte.
 */
export function RoundCard({
  round,
  playerId,
  mode,
  otherPlayerPseudo,
  onSubmit,
  onNext,
}: RoundCardProps) {
  const [text, setText] = useState('');
  const [guess, setGuess] = useState('');
  const [mediaFormat, setMediaFormat] = useState<MediaFormat>('TEXT');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset complete a chaque nouveau round
  useEffect(() => {
    setText('');
    setGuess('');
    setMediaFormat('TEXT');
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [round.id]);

  // Genere/cleanup l'URL de preview quand mediaFile change
  useEffect(() => {
    if (!mediaFile) {
      setMediaPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(mediaFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const isGuessMode = mode === 'GUESS';
  const isDareQuestion = Boolean(round.question.tagDare);
  // Si la question est Dare ET on n'est pas en mode GUESS (qui prend toute l'UI),
  // on active le selecteur de format. Sinon : texte uniquement.
  const allowMediaFormat = isDareQuestion && !isGuessMode;

  const isRevealed = round.status === 'REVEALED';
  const hasMyAnswer = round.myAnswerText !== undefined && round.myAnswerText !== null;
  const isWaitingOther = round.status === 'WAITING_OTHER' || (!isRevealed && hasMyAnswer);
  const isLast = round.order >= round.total;

  // Validation : pour text, requis. Pour media, file requis. En GUESS, guess requis aussi.
  const canSubmit = (() => {
    if (!playerId || submitting) return false;
    if (text.trim().length === 0) return false;
    if (isGuessMode && guess.trim().length === 0) return false;
    if (mediaFormat !== 'TEXT' && !mediaFile) return false;
    return true;
  })();

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const trimmedText = text.trim();
      const trimmedGuess = isGuessMode ? guess.trim() : undefined;
      if (mediaFormat === 'TEXT' || !mediaFile) {
        await onSubmit(trimmedText, trimmedGuess);
      } else {
        await onSubmit(
          trimmedText,
          trimmedGuess,
          mediaFile,
          mediaFormat === 'PHOTO' ? 'PHOTO' : 'AUDIO',
        );
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1">
        <p className="text-sm uppercase tracking-wider text-muted-foreground">
          Question {round.order} / {round.total}
        </p>
        <p className="text-xs text-muted-foreground">
          Intensité {round.question.intensity}
          {isGuessMode && <span className="ml-2 text-primary">- Mode Devine</span>}
          {isDareQuestion && <span className="ml-2 text-primary">- Défi</span>}
        </p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
        >
          <Card className="border-primary/40">
            <CardHeader>
              <CardTitle className="text-xl text-center leading-relaxed">
                {round.question.text}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isRevealed && !isWaitingOther && (
                <>
                  {allowMediaFormat && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium">Format de ta réponse</p>
                      <div className="grid grid-cols-3 gap-2">
                        <FormatButton
                          label="Texte"
                          active={mediaFormat === 'TEXT'}
                          onClick={() => {
                            setMediaFormat('TEXT');
                            setMediaFile(null);
                          }}
                        />
                        <FormatButton
                          label="Photo"
                          active={mediaFormat === 'PHOTO'}
                          onClick={() => {
                            setMediaFormat('PHOTO');
                            setMediaFile(null);
                          }}
                        />
                        <FormatButton
                          label="Audio"
                          active={mediaFormat === 'AUDIO'}
                          onClick={() => {
                            setMediaFormat('AUDIO');
                            setMediaFile(null);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="answer-textarea" className="block text-xs font-medium">
                      {mediaFormat === 'TEXT' ? 'Ta réponse' : 'Un mot pour accompagner (court)'}
                    </label>
                    <textarea
                      id="answer-textarea"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={
                        mediaFormat === 'TEXT' ? 'Ta réponse...' : 'Ex: un indice, un contexte...'
                      }
                      rows={mediaFormat === 'TEXT' ? 3 : 2}
                      maxLength={1000}
                      className="w-full rounded-md border border-input bg-input/40 px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                      autoFocus
                    />
                  </div>

                  {mediaFormat !== 'TEXT' && (
                    <div className="space-y-2">
                      <label htmlFor="media-input" className="block text-xs font-medium">
                        {mediaFormat === 'PHOTO' ? 'Ta photo (5 Mo max)' : 'Ton audio (5 Mo max)'}
                      </label>
                      <input
                        ref={fileInputRef}
                        id="media-input"
                        type="file"
                        accept={mediaFormat === 'PHOTO' ? 'image/*' : 'audio/*'}
                        capture={mediaFormat === 'PHOTO' ? 'user' : undefined}
                        onChange={(e) => {
                          const f = e.target.files?.[0] ?? null;
                          setMediaFile(f);
                        }}
                        className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/15 file:text-primary hover:file:bg-primary/25 file:cursor-pointer"
                      />
                      {mediaPreviewUrl && mediaFormat === 'PHOTO' && (
                        <img
                          src={mediaPreviewUrl}
                          alt="Aperçu de ta photo"
                          className="mt-2 max-h-48 rounded-md mx-auto"
                        />
                      )}
                      {mediaPreviewUrl && mediaFormat === 'AUDIO' && (
                        <audio src={mediaPreviewUrl} controls className="mt-2 w-full" />
                      )}
                    </div>
                  )}

                  {isGuessMode && (
                    <div className="space-y-2">
                      <label htmlFor="guess-textarea" className="block text-xs font-medium">
                        Ce que tu penses que {otherPlayerPseudo ?? "l'autre"} va répondre
                      </label>
                      <textarea
                        id="guess-textarea"
                        value={guess}
                        onChange={(e) => setGuess(e.target.value)}
                        placeholder="Sa réponse selon toi..."
                        rows={3}
                        maxLength={1000}
                        className="w-full rounded-md border border-input bg-input/40 px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                      />
                    </div>
                  )}

                  <Button
                    size="lg"
                    className="w-full min-h-[44px]"
                    onClick={() => {
                      void handleSubmit();
                    }}
                    disabled={!canSubmit}
                  >
                    {submitting
                      ? mediaFormat === 'TEXT'
                        ? 'Envoi...'
                        : 'Upload en cours...'
                      : 'Envoyer'}
                  </Button>
                  {submitError && (
                    <p className="text-xs text-destructive text-center" role="alert">
                      {submitError}
                    </p>
                  )}
                  {!playerId && (
                    <p className="text-xs text-destructive text-center">
                      Player ID introuvable - rejoins la partie à nouveau
                    </p>
                  )}
                </>
              )}

              {isWaitingOther && (
                <div className="py-8 text-center space-y-2" aria-live="polite">
                  <p className="text-primary font-medium">Tu as répondu</p>
                  <p className="text-sm text-muted-foreground animate-pulse">
                    En attente de l'autre joueur...
                  </p>
                  {round.myAnswerText && (
                    <p className="text-xs text-muted-foreground italic pt-3 max-w-xs mx-auto">
                      « {round.myAnswerText} »
                    </p>
                  )}
                </div>
              )}

              {isRevealed && (
                <div className="space-y-3" aria-live="polite">
                  {round.answers.map((a, i) => (
                    <motion.div
                      key={a.playerId}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.32,
                        delay: i * 0.12,
                        ease: 'easeOut',
                      }}
                      className="rounded-md border border-border bg-background/50 p-3 space-y-2"
                    >
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">
                        {a.pseudo}
                      </p>
                      {a.text && <p className="text-sm whitespace-pre-wrap">{a.text}</p>}

                      {a.media?.type === 'PHOTO' && (
                        <img
                          src={a.media.url}
                          alt={`Photo de ${a.pseudo}`}
                          className="max-h-64 rounded-md mx-auto"
                        />
                      )}
                      {a.media?.type === 'AUDIO' && (
                        <audio src={a.media.url} controls className="w-full" />
                      )}

                      {a.guessByOthers && a.guessByOthers.length > 0 && (
                        <div className="pt-2 border-t border-border/60 space-y-1">
                          {a.guessByOthers.map((g) => (
                            <p key={g.playerId} className="text-xs text-muted-foreground italic">
                              {g.pseudo} pensait : « {g.text} »
                            </p>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: round.answers.length * 0.12 + 0.15, duration: 0.25 }}
                  >
                    <Button size="lg" className="w-full min-h-[44px]" onClick={onNext}>
                      {isLast ? 'Terminer la partie' : 'Question suivante'}
                    </Button>
                  </motion.div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

interface FormatButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FormatButton({ label, active, onClick }: FormatButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md border px-3 py-2 text-xs font-medium transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-border bg-background/40 hover:border-primary/50'
      }`}
    >
      {label}
    </button>
  );
}
